import { Skeleton } from "@/components/ui/skeleton";

const SkeletonLoading = () => {
    return (
        <div className="w-full max-w-7xl mx-auto space-y-4">
            {/* COMPACT HEADER SKELETON */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-6 w-24" />
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-4 w-2" />
                            <Skeleton className="h-4 w-36" />
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-32" />
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-4 w-1" />
                            <Skeleton className="h-4 w-20" />
                        </div>
                    </div>
                </div>
            </div>

            {/* CALL RESULT OVERVIEW SKELETON */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <Skeleton className="h-6 w-40 mb-4" />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Outcome Status Skeleton */}
                    <div className="flex items-center gap-3">
                        <Skeleton className="w-5 h-5 rounded-full" />
                        <div className="space-y-1">
                            <Skeleton className="h-3 w-16" />
                            <Skeleton className="h-4 w-20" />
                        </div>
                    </div>

                    {/* Quality Score Skeleton */}
                    <div className="flex items-center gap-3">
                        <Skeleton className="w-5 h-5 rounded-full" />
                        <div className="space-y-1">
                            <Skeleton className="h-3 w-14" />
                            <Skeleton className="h-4 w-12" />
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT SKELETON - CALL SUMMARY & AGENT FEEDBACK */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Call Summary Skeleton */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <Skeleton className="h-4 w-24 mb-3" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                    </div>
                </div>

                {/* Agent Feedback Skeleton */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <div className="space-y-3">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Skeleton className="w-2 h-2 rounded-full" />
                                <Skeleton className="h-3 w-16" />
                            </div>
                            <div className="space-y-1 ml-4">
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-4/5" />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Skeleton className="w-2 h-2 rounded-full" />
                                <Skeleton className="h-3 w-20" />
                            </div>
                            <div className="space-y-1 ml-4">
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-3/4" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* LEAD INSIGHTS SKELETON */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <Skeleton className="h-6 w-28 mb-4" />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column Skeleton */}
                    <div className="space-y-4">
                        {/* Intent Signals Skeleton */}
                        <div>
                            <Skeleton className="h-4 w-24 mb-2" />
                            <div className="flex flex-wrap gap-2">
                                <Skeleton className="h-6 w-32 rounded-md" />
                                <Skeleton className="h-6 w-28 rounded-md" />
                                <Skeleton className="h-6 w-24 rounded-md" />
                            </div>
                        </div>

                        {/* Concerns Skeleton */}
                        <div>
                            <Skeleton className="h-4 w-28 mb-2" />
                            <div className="flex flex-wrap gap-2">
                                <Skeleton className="h-6 w-24 rounded-md" />
                                <Skeleton className="h-6 w-20 rounded-md" />
                            </div>
                        </div>
                    </div>

                    {/* Right Column Skeleton */}
                    <div className="space-y-4">
                        {/* Sentiment Skeleton */}
                        <div>
                            <Skeleton className="h-4 w-32 mb-2" />
                            <div className="flex flex-wrap gap-2">
                                <Skeleton className="h-6 w-20 rounded-md" />
                                <Skeleton className="h-6 w-28 rounded-md" />
                            </div>
                        </div>

                        {/* Qualification Skeleton */}
                        <div>
                            <Skeleton className="h-4 w-32 mb-2" />
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <Skeleton className="w-1.5 h-1.5 rounded-full" />
                                    <Skeleton className="h-3 w-40" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Skeleton className="w-1.5 h-1.5 rounded-full" />
                                    <Skeleton className="h-3 w-32" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Skeleton className="w-1.5 h-1.5 rounded-full" />
                                    <Skeleton className="h-3 w-28" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* BELOW THE FOLD SKELETON - SECONDARY CONTENT */}
            <div className="space-y-4">
                {/* Call Recording Skeleton */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="w-4 h-4" />
                    </div>
                </div>

                {/* Topics Discussed Skeleton */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <div className="flex flex-wrap gap-2">
                        <Skeleton className="h-6 w-16 rounded-md" />
                        <Skeleton className="h-6 w-20 rounded-md" />
                        <Skeleton className="h-6 w-18 rounded-md" />
                        <Skeleton className="h-6 w-24 rounded-md" />
                        <Skeleton className="h-6 w-22 rounded-md" />
                    </div>
                </div>

                {/* Call Timeline Skeleton */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center gap-2">
                        <Skeleton className="w-4 h-4" />
                        <Skeleton className="h-4 w-24" />
                    </div>
                </div>
            </div>
        </div>

    )
}

export default SkeletonLoading;